import React, {useState, useEffect} from "react";
import {observer} from "mobx-react";
import {useStores} from "../../Hooks";
import DomainWrapper from "./DomainWrapper";
import './domainSettings.scss';
import {
    Button, CircularProgress,
    Paper,
    Select,
    Table, TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import {payPerClickStatusEnum, payPerClickTypeEnum} from "../../serverClientShared/sharedEnums";
import Paginator from "../Shared/Paginator";
import {batchOptions} from "../../enums";
import ConditionalComponent from "../Shared/ConditionalComponent";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import Utility from "../../Utility";
import Menu from "@mui/material/Menu";
import {MoreHoriz} from "@mui/icons-material";

const renderHeading = category => {
    switch (category) {
        case 'ProcedureName':
            return "knowledgeBaseItem"
        case 'CreationDate':
            return "creationDate"
        case 'ExpirationDate':
            return "expirationDate"
        case 'MaxSessions':
            return "sessionsEnabled"
        case 'SessionsUsed':
            return "sessionsUsed"
        case 'Status':
            return 'status'
        case 'RequestGenerated':
            return 'requestedDate'
        case 'RequestedBy':
            return 'requestedBy'
        default:
            return ""
    }
}

const renderStatus = statusEnum => {
    switch(statusEnum) {
        case payPerClickStatusEnum.ACTIVE:
            return 'active'
        case payPerClickStatusEnum.PAUSED:
            return 'paused'
        case payPerClickStatusEnum.DONE:
            return 'done'
        case payPerClickStatusEnum.CANCELED:
            return 'canceled'
        default:
            return '-'
    }
}

const DomainPayPerClick = observer(() => {
    const {generalStore, modalStore, procedureStore} = useStores();
    const [payPerClickMenuAnchor, setPayPerClickMenuAnchor] = useState(null);
    const [menuPayPerClick, setMenuPayPerClick] = useState(null);

    const {
        creationDate,
        expirationDate,
        status,
        approvedSortBy,
        approvedSortDirection,
        pendingSortBy,
        pendingSortDirection,
        payPerClicks,
        pendingPayPerClicks,
        pageNumber,
        total,
        batchIndex,
        loadingApproved,
        loadingPending,
    } = generalStore.payPerClickSharing;

    const setPayPerClickData = (value, prop) => generalStore.payPerClickSharing[prop] = value;

    useEffect(() => {
        setPayPerClickData(true, "loadingApproved");
        setPayPerClickData(true, "loadingPending");
        generalStore.getAllPayPerClicks(true).then(success => {
            if (!!success) {
                setPayPerClickData(false, "loadingApproved");
                setPayPerClickData(false, "loadingPending");
            } else {
                modalStore.showErrorMessage(generalStore.translate('problemLoadingPayPerClickSharing'));
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const searchPrev = async () => await handleChangeApprovedFilterSort(pageNumber - 1, "pageNumber")

    const searchNext = async () => await handleChangeApprovedFilterSort(pageNumber + 1, "pageNumber")

    const getMin = () => pageNumber * batchOptions[batchIndex] + 1

    const getMax = () => Math.min((pageNumber + 1) * batchOptions[batchIndex], total)

    const handleSelectBatchNum = async event => {
        setPayPerClickData(event.target.value, "batchIndex");
        await handleChangeApprovedFilterSort(0, "pageNumber");
    }

    const handleChangeApprovedFilterSort = async (value, prop, reset = false) => {
        setPayPerClickData(value, prop);
        setPayPerClickData(true, "loadingApproved");
        await generalStore.getAllPayPerClicks(false);
        if(!!reset) {
            setPayPerClickData(0, "pageNumber")
        }
        setPayPerClickData(false, "loadingApproved");
    }

    const renderApprovedFilters = () => {
        return (
            <div className="approved-filter-container">
                <div className="filter-dropdowns">
                    <FormControl>
                        <div className='text-field-label'>{generalStore.translate('status')}</div>
                        <Select
                            variant="outlined"
                            className='filter-select theme-input-field'
                            value={status}
                            onChange={e => handleChangeApprovedFilterSort(e.target.value, "status", true)}
                            MenuProps={{
                                getContentAnchorEl: null,
                                anchorOrigin: {
                                    vertical: "bottom",
                                    horizontal: "center",
                                }
                            }}
                        >
                            <MenuItem value={'all'}>All</MenuItem>
                            <MenuItem value={payPerClickStatusEnum.ACTIVE}>{generalStore.translate('active')}</MenuItem>
                            <MenuItem value={payPerClickStatusEnum.PAUSED}>{generalStore.translate('paused')}</MenuItem>
                            <MenuItem value={payPerClickStatusEnum.DONE}>{generalStore.translate('done')}</MenuItem>
                            <MenuItem
                                value={payPerClickStatusEnum.CANCELED}>{generalStore.translate('canceled')}</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl>
                        <div className='text-field-label'>{generalStore.translate('creationDate')}</div>
                        <Select
                            variant="outlined"
                            labelId='approved-creation-filter-label'
                            className='filter-select theme-input-field'
                            value={creationDate}
                            onChange={e => handleChangeApprovedFilterSort(e.target.value, 'creationDate', true)}
                            MenuProps={{
                                getContentAnchorEl: null,
                                anchorOrigin: {
                                    vertical: "bottom",
                                    horizontal: "center",
                                }
                            }}
                        >
                            <MenuItem value={0}>{generalStore.translate('today')}</MenuItem>
                            <MenuItem value={1}>{generalStore.translate('yesterday')}</MenuItem>
                            <MenuItem value={7}>{generalStore.translate('last7Days')}</MenuItem>
                            <MenuItem value={30}>{generalStore.translate('last30Days')}</MenuItem>
                            <MenuItem value={-1}>{generalStore.translate('all')}</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl>
                        <div className='text-field-label'>{generalStore.translate('expirationDate')}</div>
                        <Select
                            variant="outlined"
                            labelId='approved-expiration-filter-label'
                            className='filter-select theme-input-field'
                            value={expirationDate}
                            onChange={e => handleChangeApprovedFilterSort(e.target.value, 'expirationDate', true)}
                            MenuProps={{
                                getContentAnchorEl: null,
                                anchorOrigin: {
                                    vertical: "bottom",
                                    horizontal: "center",
                                }
                            }}
                        >
                            <MenuItem value={0}>{generalStore.translate('today')}</MenuItem>
                            <MenuItem value={7}>{generalStore.translate('next7Days')}</MenuItem>
                            <MenuItem value={30}>{generalStore.translate('next30Days')}</MenuItem>
                            <MenuItem value={180}>{generalStore.translate('next6Months')}</MenuItem>
                            <MenuItem value={360}>{generalStore.translate('nextYear')}</MenuItem>
                            <MenuItem value={-1}>{generalStore.translate('recurring')}</MenuItem>
                            <MenuItem value={-2}>{generalStore.translate('all')}</MenuItem>
                        </Select>
                    </FormControl>
                </div>
                <div className="pagination-menu">
                    <Paginator
                        onPreviousClick={searchPrev}
                        onNextClick={searchNext}
                        currentMin={getMin()}
                        currentMax={getMax()}
                        total={total}
                    />
                    <TextField
                        value={batchIndex}
                        className="select-batch-num theme-input-field"
                        select
                        onChange={handleSelectBatchNum}
                        variant="outlined"
                    >
                        {batchOptions.map((option, i) => (
                            <MenuItem key={option} value={i} className="comments-batch-num">
                                {option}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Typography
                        component="span"
                        variant="subtitle2"
                        color="textPrimary"
                    >
                        {generalStore.translate('perPage')}
                    </Typography>
                </div>
            </div>
        )
    }

    const renderTitleAndSortIcon = (category, isApproved = false) => {
        const sortBy = isApproved ? approvedSortBy : pendingSortBy;
        const sortDirection = isApproved ? approvedSortDirection : pendingSortDirection;

        const selectSort = () => {
            if (sortBy === category) {
                setPayPerClickData(sortDirection * -1, isApproved ? "approvedSortDirection" : "pendingSortDirection")
            } else setPayPerClickData(category, isApproved ? "approvedSortBy" : "pendingSortBy")
            if(isApproved) {
                setPayPerClickData(true, "loadingApproved");
                generalStore.getAllPayPerClicks(false).then(() => setPayPerClickData(false, "loadingApproved"));
            }
        }

        return (
            <div
                className={`pay-per-click-sort-container ${category} ${sortBy === category ? " selected" : ""}`}
                onClick={selectSort}
            >
                <div
                    className={sortBy === category ? "selected" : ""}>{generalStore.translate(renderHeading(category))}</div>
                <ConditionalComponent if={sortBy === category}>
                    {sortDirection === -1
                        ? <ArrowDownwardIcon/>
                        : <ArrowUpwardIcon/>
                    }
                </ConditionalComponent>
                <ConditionalComponent if={sortBy !== category}>
                    <div className="empty-box"/>
                </ConditionalComponent>
            </div>
        )
    }

    const renderApprovedTable = () => {
        return (
            <>
                <TableContainer component={"div"} className='pay-per-click-table-main'>
                    <ConditionalComponent if={loadingApproved}>
                            <div className='loader-container'>
                                <CircularProgress color='primary' variant='indeterminate'/>
                            </div>
                    </ConditionalComponent>
                    <Table className='pay-per-click-table' aria-label="pay per click approved table" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    {renderTitleAndSortIcon("ProcedureName", true)}
                                </TableCell>
                                <TableCell>
                                    {renderTitleAndSortIcon("CreationDate", true)}
                                </TableCell>
                                <TableCell>
                                    {renderTitleAndSortIcon("ExpirationDate", true)}
                                </TableCell>
                                <TableCell>
                                    {renderTitleAndSortIcon("MaxSessions", true)}
                                </TableCell>
                                <TableCell>
                                    {renderTitleAndSortIcon("SessionsUsed", true)}
                                </TableCell>
                                <TableCell>
                                    {renderTitleAndSortIcon("Status", true)}
                                </TableCell>
                                <TableCell/>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {renderApprovedTableRows()}
                        </TableBody>
                    </Table>
                </TableContainer>
            </>
        )
    }

    const renderApprovedTableRows = () => {
        return payPerClicks.map((payPerClick) => {
            return (
                <TableRow
                    key={payPerClick._id}
                    className={`task-row ${payPerClick.IsCanceled ? " canceled" : ""}`}
                >
                    <TableCell>{payPerClick.ProcedureName}</TableCell>
                    <TableCell>{Utility.renderDate_DDMMYYY(payPerClick.CreationDate)}</TableCell>
                    <TableCell>
                        {payPerClick.SharingType === payPerClickTypeEnum.RECURRING
                            ? generalStore.translate('recurring')
                            : payPerClick.ExpirationDate < new Date()
                                ? generalStore.translate('nA')
                                : Utility.renderDate_DDMMYYY(payPerClick.ExpirationDate)
                        }
                    </TableCell>
                    <TableCell>
                        {payPerClick.IsActive
                            ? Utility.getBigNumberString(payPerClick.MaxSessions || 0)
                            : generalStore.translate('nA')
                        }
                    </TableCell>
                    <TableCell>{Utility.getBigNumberString(payPerClick.SessionsUsed || 0)}</TableCell>
                    <TableCell>{generalStore.translate(renderStatus(payPerClick.Status))}</TableCell>
                    <TableCell>
                        {!payPerClick.IsCanceled
                            ? renderPayPerClickOptionsMenu(payPerClick)
                            : null
                        }
                    </TableCell>
                </TableRow>
            )
        });
    }

    const renderPendingTable = () => {
        return (
            <>
                <TableContainer component={"div"} className='pay-per-click-table-main'>
                    <ConditionalComponent if={loadingPending}>
                        <div className='loader-container'>
                            <CircularProgress color='primary' variant='indeterminate'/>
                        </div>
                    </ConditionalComponent>
                    <Table className='pay-per-click-table' aria-label="pending pay per click requests table" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    {renderTitleAndSortIcon("ProcedureName", false)}
                                </TableCell>
                                <TableCell>
                                    {renderTitleAndSortIcon("RequestGenerated", false)}
                                </TableCell>
                                <TableCell>
                                    {renderTitleAndSortIcon("RequestedBy", false)}
                                </TableCell>
                                <TableCell/>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {renderPendingTableRows()}
                        </TableBody>
                    </Table>
                </TableContainer>
            </>
        )
    }

    const renderPendingTableRows = () => {
        const sortedPending = [...pendingPayPerClicks].sort((a,b) => {
            if (a[pendingSortBy] < b[pendingSortBy]) {
                return pendingSortDirection;
            }
            if (a[pendingSortBy] > b[pendingSortBy]) {
                return pendingSortDirection * -1;
            }
            return 0;
        });

        return sortedPending.map((payPerClick) => {
            return (
                <TableRow
                    key={payPerClick._id}
                    className='task-row'
                >
                    <TableCell>{payPerClick.ProcedureName}</TableCell>
                    <TableCell>{Utility.renderDate_DDMMYYY(payPerClick.RequestGenerated)}</TableCell>
                    <TableCell>{Utility.getUserFullName(payPerClick.RequestedBy)}</TableCell>
                    <TableCell>
                        <div className="pending-pay-per-click-btns">
                            <Button
                                className="pending-pay-per-click-btn allow"
                                onClick={() => allowPending(payPerClick)}
                            >
                                {generalStore.translate('allow')}
                            </Button>
                            <Button
                                className="pending-pay-per-click-btn decline"
                                onClick={() => declinePending(payPerClick)}
                            >
                                {generalStore.translate('decline')}
                            </Button>
                        </div>
                    </TableCell>
                </TableRow>
            )
        });
    }

    const allowPending = payPerClick => {
        const toUpdate = {...payPerClick}
        toUpdate.IsPending = false;
        toUpdate.IsActive = true;
        toUpdate.CreationDate = new Date();
        toUpdate.ApprovedBy = generalStore.userDetails._id;
        closePayPerClickMenu();
        modalStore.modalData = {
            payPerClick: toUpdate,
            afterSave: async () => {
                setPayPerClickData(true, "loadingApproved");
                setPayPerClickData(true, "loadingPending");
                await generalStore.getAllPayPerClicks(true);
                setPayPerClickData(false, "loadingApproved");
                setPayPerClickData(false, "loadingPending");
            }
        };
        modalStore.isPayPerClickSharingOpen = true;
    }

    const declinePending = async payPerClick => {
        setPayPerClickData(true, "loadingPending");
        await procedureStore.deletePendingPayPerClick(payPerClick._id);
        await generalStore.getAllPayPerClicks(true);
        setPayPerClickData(false, "loadingPending");
    }

    const renderPayPerClickOptionsMenu = payPerClick => {
        return (
            <div
                className="pay-per-click-menu-container"
            >
                <MoreHoriz
                    onClick={(e) => openPayPerClickMenu(e, payPerClick)}
                    name="pay-per-click-three-dot-icon"
                />
                <Menu
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: generalStore.langDir === 'rtl' ? "left" : "right",
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: generalStore.langDir === 'rtl' ? "left" : "right",
                    }}
                    open={!!payPerClickMenuAnchor && menuPayPerClick._id === payPerClick._id}
                    onClose={closePayPerClickMenu}
                    anchorEl={payPerClickMenuAnchor}
                    className='pay-per-click-menu'
                    disableAutoFocusItem
                    getContentAnchorEl={null}
                >
                    <MenuItem className='dropdown-item' onClick={() => openEditPayPerClick(payPerClick)}>
                        <span>{generalStore.translate('edit')}</span>
                    </MenuItem>
                    <MenuItem className='dropdown-item' onClick={() => openPayPerClickAnalytics(payPerClick)}>
                        <span>{generalStore.translate('analytics')}</span>
                    </MenuItem>
                    <ConditionalComponent if={payPerClick.Status < payPerClickStatusEnum.DONE}>
                        <MenuItem className='dropdown-item' onClick={() => togglePausePayPerClick(payPerClick)}>
                            <span>{generalStore.translate(payPerClick.IsActive ? 'pausePermission' : 'unpause')}</span>
                        </MenuItem>
                    </ConditionalComponent>
                    <MenuItem className='dropdown-item warning' onClick={() => cancelPayPerClick(payPerClick)}>
                        <span>{generalStore.translate('stopPermission')}</span>
                    </MenuItem>
                </Menu>
            </div>
        );
    }

    const openPayPerClickMenu = (e, payPerClick) => {
        setPayPerClickMenuAnchor(e.currentTarget);
        setMenuPayPerClick(payPerClick);
    }

    const closePayPerClickMenu = () => {
        setPayPerClickMenuAnchor(null);
        setMenuPayPerClick(null);
    }

    const openEditPayPerClick = payPerClick => {
        payPerClick.IsRecurring = payPerClick.SharingType === payPerClickTypeEnum.RECURRING;
        closePayPerClickMenu();
        modalStore.modalData = {
            payPerClick : {...payPerClick},
            afterSave: async () => {
                setPayPerClickData(true, "loadingApproved");
                await generalStore.getAllPayPerClicks(false);
                setPayPerClickData(false, "loadingApproved");
            }
        };
        modalStore.isPayPerClickSharingOpen = true;
    }

    const openPayPerClickAnalytics = payPerClick => {
        closePayPerClickMenu();
        modalStore.modalData = {
            payPerClick,
        };
        modalStore.isPayPerClickAnalyticsOpen = true;
    }

    const togglePausePayPerClick = async payPerClick => {
        closePayPerClickMenu();
        setPayPerClickData(true, "loadingApproved");
        payPerClick.IsActive = !payPerClick.IsActive;
        await procedureStore.updatePayPerClick(payPerClick);
        await generalStore.getAllPayPerClicks(false);
        setPayPerClickData(false, "loadingApproved");
    }

    const cancelPayPerClick = async payPerClick => {
        closePayPerClickMenu();
        const toUpdate = {...payPerClick}
        modalStore.modalData = {
            title: generalStore.translate('cancelPayPerClick'),
            message: `${generalStore.translate('confirmCancelPayPerClickSentence1_beforeName')}${payPerClick.ProcedureName}${generalStore.translate('confirmCancelPayPerClickSentence2_afterName')}`,
            onConfirm: async () => {
                setPayPerClickData(true, "loadingApproved");
                toUpdate.IsActive = false;
                toUpdate.IsCanceled = true;
                await procedureStore.updatePayPerClick(toUpdate);
                await generalStore.getAllPayPerClicks(false);
                setPayPerClickData(false, "loadingApproved");
            }
        }
        modalStore.isCancelPayPerClickModalOpen = true;
    }

    const renderApprovedPayPerClickTable = () => {
        return (
            <div className="pay-per-click-main">
                <div className="pay-per-click-table-title theme-windows-and-menus-pay-per-click">{generalStore.translate('approvedItems')}</div>
                <Paper
                    elevation={1}
                    className="pay-per-click-table-container theme-windows-and-menus-pay-per-click"
                >
                    {renderApprovedFilters()}
                    {renderApprovedTable()}
                </Paper>
            </div>
        )
    }

    const renderPendingPayPerClickTable = () => {
        return (
            <div className="pay-per-click-main">
                <div className="pay-per-click-table-title theme-windows-and-menus-pay-per-click">{generalStore.translate('pendingItems')}</div>
                <Paper
                    elevation={1}
                    className="pay-per-click-table-container theme-windows-and-menus-pay-per-click"
                >
                    {renderPendingTable()}
                </Paper>
            </div>
        )
    }

    return (
        <DomainWrapper wide>
            <div className="pay-per-click-sharing-container">
                <div className="pay-per-click-main-title">{generalStore.translate('payPerClickSharing')}</div>
                {renderApprovedPayPerClickTable()}
                {renderPendingPayPerClickTable()}
            </div>
        </DomainWrapper>
    );
});

export default DomainPayPerClick;
